import React from 'react';
import { useSEO } from '../../../hooks/useSEO';
import { FC_CONTENT as C } from './content';
import WhatsAppCTA from '../SoftwareForDistributors/components/WhatsAppCTA';
import SectionFAQ from '../SoftwareForDistributors/components/SectionFAQ';
import './FinancialControlLanding.scss';

const FinancialControlLanding: React.FC = () => {
  useSEO({
    title: C.seo.title,
    description: C.seo.description,
    canonical: '/controle-financeiro-empresarial',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: '77Gestão — Controle Financeiro',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://77gestao.com.br/controle-financeiro-empresarial',
      description: C.seo.description,
    },
  });

  return (
    <div className="financial-control-landing">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="lp-hero" aria-labelledby="fc-hero-title">
        <div className="lp-container">
          <span className="lp-kicker">{C.hero.kicker}</span>
          <div className="lp-hero-title-wrapper">
            <h1 className="lp-title" id="fc-hero-title">{C.hero.title}</h1>
            <div className="lp-hero-icon">
              <i className="fa-solid fa-chart-line" aria-hidden="true" />
            </div>
          </div>
          <p className="lp-subtitle">{C.hero.subtitle}</p>
          <WhatsAppCTA message={C.hero.ctaMessage} id="cta-hero">
            {C.hero.cta}
          </WhatsAppCTA>
          <p className="lp-meta">{C.hero.urgency}</p>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────── */}
      <div className="fc-stats-strip" aria-hidden="true">
        <div className="lp-container">
          {C.statsStrip.map((s, i) => (
            <div className="fc-stat" key={i}>
              <i className={s.icon} />
              <span className="fc-stat__number">{s.number}</span>
              <span className="fc-stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Pain ─────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--pain" aria-labelledby="fc-pain-title">
        <div className="lp-container">
          <h2 id="fc-pain-title">{C.pain.title}</h2>
          <p className="lp-intro">{C.pain.intro}</p>
          <div className="fc-pain-groups">
            {C.pain.groups.map((group, gi) => (
              <div className="fc-pain-group" key={gi}>
                <div className="fc-pain-group__header">
                  <i className={group.icon} aria-hidden="true" />
                  <span>{group.label}</span>
                </div>
                <ul>
                  {group.items.map((item, ii) => (
                    <li key={ii}>
                      <i className={item.icon} aria-hidden="true" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mid CTA ──────────────────────────────────────────────────── */}
      <div className="fc-mid-cta">
        <div className="lp-container">
          <p>{C.midCta.text}</p>
          <WhatsAppCTA message={C.midCta.ctaMessage} id="cta-mid">
            {C.midCta.cta}
          </WhatsAppCTA>
        </div>
      </div>

      {/* ── Solution ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--solution" aria-labelledby="fc-solution-title">
        <div className="lp-container">
          <h2 id="fc-solution-title">{C.solution.title}</h2>
          <p className="lp-intro">{C.solution.intro}</p>
          <div className="lp-cards">
            {C.solution.cards.map((card, i) => (
              <article className="lp-card" key={i}>
                <div className="lp-card__icon">
                  <i className={card.icon} aria-hidden="true" />
                </div>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Proof ────────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--proof" aria-labelledby="fc-proof-title">
        <div className="lp-container">
          <h2 id="fc-proof-title">{C.proof.title}</h2>
          <p className="lp-intro">{C.proof.intro}</p>

          <div className="fc-proof-stats">
            {C.proof.stats.map((s, i) => (
              <div className="fc-proof-stat" key={i}>
                <span className="fc-proof-stat__number">{s.number}</span>
                <span className="fc-proof-stat__label">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="lp-proof-box">
            {C.proof.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            <p className="lp-proof-highlight">
              <i className="fa-solid fa-check-circle" aria-hidden="true" />
              {C.proof.highlight}
            </p>
          </div>

          <div className="lp-section-cta">
            <WhatsAppCTA message={C.proof.ctaMessage} id="cta-proof">
              {C.proof.cta}
            </WhatsAppCTA>
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────────── */}
      <section className="lp-section lp-section--benefits" aria-labelledby="fc-benefits-title">
        <div className="lp-container">
          <h2 id="fc-benefits-title">{C.benefits.title}</h2>
          <div className="lp-benefits-grid">
            {C.benefits.items.map((item, i) => (
              <article key={i}>
                <div className="lp-benefit__icon">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Differentials ────────────────────────────────────────────── */}
      <section className="lp-section lp-section--pain" aria-labelledby="fc-diff-title">
        <div className="lp-container">
          <h2 id="fc-diff-title">{C.differentials.title}</h2>
          <div className="fc-diff-grid">
            {C.differentials.items.map((item, i) => (
              <div className="fc-diff-card" key={i}>
                <div className="fc-diff-card__icon">
                  <i className={item.icon} aria-hidden="true" />
                </div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <SectionFAQ {...C.faq} />

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section className="lp-cta-final" id="schedule-demo" aria-labelledby="fc-cta-final-title">
        <div className="lp-container">
          <h2 id="fc-cta-final-title">{C.ctaFinal.title}</h2>
          <p>{C.ctaFinal.subtitle}</p>
          <WhatsAppCTA message={C.ctaFinal.ctaMessage} className="lp-btn lp-btn--final" id="cta-final">
            {C.ctaFinal.cta}
          </WhatsAppCTA>
        </div>
      </section>
    </div>
  );
};

export default FinancialControlLanding;
